<script lang="ts" context="module">
  import { writable, type Writable } from "svelte/store";

  export interface CreateCommunityContext {
    open: Writable<boolean>;
  }
  export const contextKey = Symbol();
  export const getModalContext = () => getContext<CreateCommunityContext>(contextKey);
</script>

<script lang="ts">
  import Modal from "app/components/+shared/Modal/Modal.svelte";
  import CRMForm from "$components/community/forms/CRMForm.svelte";
  import { session } from "app/stores/auth";
  import { goto } from "$app/navigation";
  import { portal } from "app/actions/portal.svelte";
  import { communitySlug, pricingPlan } from "app/stores/community";
  import { fade } from "svelte/transition";
  import { getContext, setContext } from "svelte";
  import CreateCommunityForm from "./CreateCommunityForm.svelte";
  import api from "$utils/api";
  import DynamicButton from "app/components/+shared/Button/DynamicButton/DynamicButton.svelte";
  import { Left } from "$components/+shared/OVIcon";
  import { workspaceRootUrl } from "$utils/urls";

  const STEP_CREATE = 0;
  const STEP_CRM = 1;

  let step = STEP_CREATE;
  let open = writable<boolean>(false);
  // provide join function for children to join community/login flow
  setContext(contextKey, { open });

  async function onCommunityCreate() {
    // save UTM info after community creation
    // but before additional fields to prevent loss in case of dropoff
    let patch = {} as any;
    if ($session.utm.in_session) {
      patch = {
        utmSource: $session.utm.source,
        utmMedium: $session.utm.medium,
        utmCampaign: $session.utm.campaign,
      };
    }
    // pass through pricing plan selected on marketing page
    if ($pricingPlan) {
      patch["planSelected"] = $pricingPlan;
      $pricingPlan = ""; // blank it out afterwards
    }
    if (Object.keys(patch).length) {
      await api.communitiesCrm({
        patchedCommunityCRM: patch,
        slug: $communitySlug,
      });
    }
    await goto(workspaceRootUrl({ communitySlug: $communitySlug }));
    step = STEP_CRM;
  }

  async function onCRMComplete(ev: CustomEvent<any>) {
    await api.communitiesCrm({
      patchedCommunityCRM: {
        ...ev.detail,
        sendUpdates: ev.detail.subscribe?.subscribe,
        currentDocTool: ev.detail.currentDocTool,
      },
      slug: $communitySlug,
    });
    $open = false;
    await goto(workspaceRootUrl({ communitySlug: $communitySlug }));
    // reset to create step
    step = STEP_CREATE;
  }
</script>

<slot />

<Modal
  bind:open={$open}
  backdropVariant="secondary"
  withCloseIcon={false}
  disallowClickOutside
  on:close={() => (step = STEP_CREATE)}
>
  {#if step == STEP_CREATE}
    <div class="fixed left-6 top-6 z-portal flex w-full" use:portal>
      <DynamicButton
        a11yLabel="Return to Outverse"
        iconLeft={Left}
        variant="tertiary"
        label="Return to Outverse"
        on:click={() => ($open = false)}
      />
    </div>
  {/if}

  <div
    slot="backdrop"
    class="mx-auto flex max-w-[520px] flex-col items-center justify-center text-center"
  >
    <h3 class="mb-7 text-2xl font-medium text-gray-700 md:mb-4 dark:text-gray-100">
      Get started on Outverse
    </h3>

    <div class="mb-7 hidden text-gray-500 md:block dark:text-gray-400">
      Scale support in a home that's tailor-made for software companies.
    </div>
  </div>

  {#if step == STEP_CREATE}
    <CreateCommunityForm communityCreated={() => onCommunityCreate()} />
  {:else if step == STEP_CRM}
    <div in:fade={{ duration: 400, delay: 100 }} class="h-full w-full">
      <CRMForm on:submit={onCRMComplete} />
    </div>
  {/if}
</Modal>
